import { requestApi } from 'api/api'
import dayjs from 'dayjs'
import { DateFormat } from 'constants/General'
import { Transaction } from 'types/transaction'
import {
  GetWalletTransactionsReq,
  WalletTransactionsExport,
} from 'types/transaction/GetTransactionsReq'
import { TransactionsPagination } from 'types/transaction/TransactionsPagination'
import type { ExportFileFormatType, ReportType } from 'types/transactionsExport'

export type transactionExportFileFormatType = 'csv' | 'pdf'

export interface BuildWalletExportPayloadParams {
  reportType: ReportType
  selectedExportFormat: ExportFileFormatType
  walletLSID?: string | number
  selectedDateRange?: { from: string; to: string }
  balanceDate?: string
  categories?: string[]
}

export const buildWalletExportPayload = ({
  reportType,
  selectedExportFormat,
  walletLSID,
  selectedDateRange,
  balanceDate,
  categories,
}: BuildWalletExportPayloadParams): WalletTransactionsExport => {
  const isBalanceReport = reportType === 'BALANCE'

  return {
    selectedExportFormat,
    walletId: walletLSID,
    reportType,
    categories,
    ...(isBalanceReport
      ? {
          balanceDate: balanceDate
            ? dayjs(balanceDate, DateFormat.DD_COMMA_MM_COMMA_YYYY).format(DateFormat.YYYY_MM_DD) +
              'T23:59:59'
            : undefined,
        }
      : {
          selectedDateRange: {
            from: `${selectedDateRange?.from}T00:00:00`,
            to: `${selectedDateRange?.to}T23:59:59`,
          },
        }),
  }
}

export const getWalletTransactions = async ({
  walletId,
  offset,
  max,
}: GetWalletTransactionsReq): Promise<TransactionsPagination> => {
  const { data, headers }: { data: Transaction[]; headers: { [key: string]: string } } =
    await requestApi({
      url: `/transaction/${walletId}`,
      method: 'GET',
      params: {
        ...(!!offset && { offset }),
        ...(!!max && { max }),
      },
      fullResponse: true,
    })
  return {
    transactions: data || [],
    paging: {
      total: Number(headers['x-api-list-total']),
      offset: Number(headers['x-api-list-start']),
      max,
    },
  }
}

export const sendTransactionsReport = ({
  selectedExportFormat,
  walletId,
  selectedDateRange,
  reportType = 'TRANSACTION',
  balanceDate,
  categories,
}: WalletTransactionsExport): Promise<void> => {
  const dateParams =
    reportType === 'BALANCE'
      ? { at: balanceDate }
      : { from: selectedDateRange?.from, to: selectedDateRange?.to }

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v1/reports',
    data: {
      ...dateParams,
      format: selectedExportFormat,
      deliveryChannel: 'EMAIL',
      type: reportType,
      ...(!!walletId && { walletId }),
      ...(categories?.length > 0 && { categories }),
    },
    method: 'POST',
  })
}
